import {
  CheckboxGroup as HeroUICheckboxGroup,
  CheckboxGroupProps as HeroUICheckboxGroupProps,
} from "@heroui/react";
import clsx from "clsx";
import { getKey } from "core/model/utils/strings";
import { Checkbox, CheckboxProps } from "../checkbox/checkbox";

type CheckboxOptionWithSubForm = Omit<CheckboxProps, "name" | "id"> & {
  subForm?: (props: {
    isDisabled?: boolean;
    size?: CheckboxProps["size"];
  }) => React.ReactNode;
};

export type CheckboxGroupProps = Omit<
  HeroUICheckboxGroupProps,
  "children" | "size" | "color"
> & {
  color?: CheckboxProps["color"];
  name: string;
  options?: CheckboxOptionWithSubForm[];
  size?: CheckboxProps["size"];
};

export function CheckboxGroup({
  onChange,
  onValueChange,
  options,
  value,
  ...props
}: CheckboxGroupProps) {
  // Handle value changes - call both form handler and optional onChange
  const handleValueChange = (newValue: string[]) => {
    onValueChange?.(newValue);
    onChange?.(newValue);
  };

  return (
    <HeroUICheckboxGroup
      {...props}
      data-testid={props.name}
      onValueChange={handleValueChange}
      value={value}
      classNames={{
        label: clsx(
          "text-base font-semibold text-default-900 after:text-gray-900",
          {
            "text-sm": props.size === "sm",
          },
        ),
        description: "text-gray-600",
        errorMessage: "text-danger-dark",
      }}
    >
      {options?.map((option, i) => {
        const { subForm, ...checkboxProps } = option;
        const isSelected = !!(option.value && value?.includes(option.value));

        return (
          <div key={getKey(option.value, i)} className="flex flex-col gap-3">
            <Checkbox
              key={getKey(option.value, i)}
              {...checkboxProps}
              isDisabled={props.isDisabled || option.isDisabled}
              name={props.name}
              size={props.size}
            />
            {subForm && (
              <div
                className={clsx("mb-2 pl-6 transition-all duration-200", {
                  "opacity-100": isSelected,
                  "hidden opacity-0": !isSelected,
                })}
              >
                {subForm({
                  size: props.size,
                  isDisabled: props.isDisabled,
                })}
              </div>
            )}
          </div>
        );
      })}
    </HeroUICheckboxGroup>
  );
}

CheckboxGroup.displayName = "RecareUI.CheckboxGroup";
